import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { useOutsideClick } from "../utils/useOutsideClick";
import { displayToast } from "../utils/Toast";
import ProductMaster from "./master/ProductMaster";
import CustomerMasterDataModal from "./master/CustomerMasterDataModal";
import VendorMasterDataModal from "./master/VendorMasterDataModal";
import {
  fetchVendorCategoriesList,
  fetchSubcategoriesByCategoryId,
  fetchVendorsBySubcategory,
} from "../apis/transaction";
import "../styles/components/AddNewEntryModal.css";

const AddNewEntryModal = ({
  isOpen,
  onClose,
  onSubmit,
  heading,
  placeholder,
  options,
  onCompanySelect,
  transactionCode,
  customerType,
  searchPlaceholder,
  subheading,
  isPurchaseInquiryVendorFlow = false,
}) => {
  const [formData, setFormData] = useState({
    companyName: "",
    buyerName: "",
  });
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [showMasterModal, setShowMasterModal] = useState(false);
  const modalRef = useOutsideClick(() => {
    if (!showMasterModal) {
      setDropdownOpen(false);
      onClose();
    }
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [subcategories, setSubcategories] = useState([]);
  const [subcategorySearchTerm, setSubcategorySearchTerm] = useState("");
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [isVendorLoading, setIsVendorLoading] = useState(false);

  const getOptionLabel = (option) => {
    if (!option) return "";
    return (
      option?.display_name ||
      option?.company_name ||
      option?.name ||
      option?.label ||
      ""
    );
  };

  const getSubcategoryLabel = (sub) => {
    return sub?.subcategory_name || sub?.display_name || sub?.name || "";
  };

  const normalizeVendors = (items = []) => {
    return (items || []).map((item) => {
      if (typeof item === "string") {
        return { display_name: item };
      }
      if (item?.display_name) {
        return item;
      }
      return {
        ...item,
        display_name: item?.company_name || item?.name || "",
      };
    });
  };

  const uniqueByTranOrName = (items = []) => {
    const seen = new Set();
    return items.filter((item) => {
      const key = item?.tran_id || getOptionLabel(item);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  useEffect(() => {
    if (!isOpen) {
      setFormData({ companyName: "", buyerName: "" });
      setSearchTerm("");
      setDropdownOpen(false);
      setCategoryOptions([]);
      setSelectedCategory(null);
      setCategoryDropdownOpen(false);
      setSubcategories([]);
      setSubcategorySearchTerm("");
      setSelectedSubcategories([]);
      setVendors([]);
      setIsVendorLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const loadCategories = async () => {
      if (!isOpen || !isPurchaseInquiryVendorFlow) return;
      try {
        const response = await fetchVendorCategoriesList();
        const items =
          response?.items ||
          response?.data ||
          (Array.isArray(response) ? response : []);
        setCategoryOptions(items);
      } catch (error) {
        displayToast("Failed to fetch categories", "error");
      }
    };

    loadCategories();
  }, [isOpen, isPurchaseInquiryVendorFlow]);

  const handleInputClick = () => {
    setDropdownOpen((prevState) => !prevState);
  };

  const handleAddNewCustomer = (e) => {
    e.stopPropagation();
    setShowMasterModal(true);
    setDropdownOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  const handleMasterModalClose = () => {
    setShowMasterModal(false);
  };

  const handleCategorySelect = async (category) => {
    setSelectedCategory(category);
    setCategoryDropdownOpen(false);
    setSubcategorySearchTerm("");
    setVendors([]);
    try {
      const response = await fetchSubcategoriesByCategoryId(category.id);
      const items =
        response?.items ||
        response?.data ||
        (Array.isArray(response) ? response : []);
      setSubcategories(items);
    } catch (error) {
      setSubcategories([]);
      displayToast("Failed to fetch subcategories", "error");
    }
  };

  const removeSubcategory = (subcategoryId) => {
    setSelectedSubcategories((prev) =>
      prev.filter((item) => item.id !== subcategoryId)
    );
    setVendors([]);
  };

  const toggleSubcategory = (subcategory) => {
    const id = subcategory?.id;
    if (!id) return;
    setSelectedSubcategories((prev) => {
      const exists = prev.some((item) => item.id === id);
      if (exists) {
        return prev.filter((item) => item.id !== id);
      }
      return [...prev, {
        ...subcategory,
        categoryName: selectedCategory?.category_name || selectedCategory?.name || "Unknown"
      }];
    });
    setVendors([]);
  };

  const handleSearchVendor = async () => {
    if (selectedSubcategories.length === 0) {
      displayToast("Please select at least one subcategory from any category", "error");
      return;
    }

    setIsVendorLoading(true);
    try {
      const subcategoryQuery = selectedSubcategories
        .map((subcategory) => getSubcategoryLabel(subcategory))
        .filter(Boolean)
        .join(",");

      const response = await fetchVendorsBySubcategory(subcategoryQuery);
      const backendVendors = normalizeVendors(response?.items || []);
      const uniqueVendors = uniqueByTranOrName(backendVendors);
      setVendors(uniqueVendors);

      if (uniqueVendors.length === 0) {
        displayToast("No vendors available for selected subcategories", "error");
      }
    } catch (error) {
      setVendors([]);
      displayToast("Failed to fetch vendors", "error");
    } finally {
      setIsVendorLoading(false);
    }
  };

  const handlePurchaseModalClick = () => {
    if (categoryDropdownOpen) {
      setCategoryDropdownOpen(false);
    }
  };

  const renderMasterModal = () => {
    if (customerType === "customer") {
      return (
        <CustomerMasterDataModal
          transactionCode="CM"
          newEntryTranCode={transactionCode}
          heading="Add New Customer"
          modeOfEdit="add"
          onClose={handleMasterModalClose}
          customerType={customerType}
          isPopupMode={true}
        />
      );
    } else if (customerType === "vendor") {
      return (
        <VendorMasterDataModal
          transactionCode="VM"
          newEntryTranCode={transactionCode}
          heading="Add New Vendor"
          modeOfEdit="add"
          onClose={handleMasterModalClose}
          customerType={customerType}
          isPopupMode={true}
        />
      );
    }
    return (
      <ProductMaster
        transactionCode={customerType === "customer" ? "CM" : "VM"}
        newEntryTranCode={transactionCode}
        heading={`Add New ${customerType === "customer" ? "Customer" : "Vendor"
          }`}
        modeOfEdit="add"
        onClose={handleMasterModalClose}
        customerType={customerType}
        isPopupMode={true}
      />
    );
  };

  if (!isOpen) return null;

  if (isPurchaseInquiryVendorFlow) {
    const filteredSubcategories = (subcategories || []).filter((sub) =>
      getSubcategoryLabel(sub)
        .toLowerCase()
        .includes(subcategorySearchTerm.toLowerCase())
    );

    return (
      <div className="modal-overlay" style={{ zIndex: 1000 }}>
        <div className="modal-content" ref={modalRef} onClick={handlePurchaseModalClick}>
          <div className="modal-header">
            <h2>{heading}</h2>
            <button
              className="close-button"
              onClick={onClose}
              style={{ marginTop: 4 }}
            >
              &times;
            </button>
          </div>

          <div className="add-new-form-container">
            <div className="purchase-enquiry-layout">
              <div className="purchase-enquiry-row">
                <div
                  className="purchase-enquiry-category-select"
                  onClick={(e) => e.stopPropagation()}
                >
                  <label className="purchase-enquiry-label">Category</label>
                  <div
                    className="input-with-arrow"
                    onClick={() =>
                      setCategoryDropdownOpen((prevState) => !prevState)
                    }
                  >
                    <input
                      type="text"
                      value={selectedCategory?.category_name || ""}
                      placeholder="Select category"
                      readOnly
                      className="company-input"
                    />
                    <span className="dropdown-arrow">&#9662;</span>
                  </div>

                  {categoryDropdownOpen && (
                    <div className="dropdown">
                      {categoryOptions.map((categoryOption) => (
                        <div
                          key={categoryOption.id}
                          className="add-item-dropdown-option"
                          onClick={() => handleCategorySelect(categoryOption)}
                        >
                          {categoryOption.category_name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="purchase-enquiry-sub-search">
                  <label className="purchase-enquiry-label">Search Subcategory</label>
                  <input
                    type="text"
                    className="dropdown-search-input"
                    placeholder="Search subcategory"
                    value={subcategorySearchTerm}
                    onChange={(e) => setSubcategorySearchTerm(e.target.value)}
                    disabled={!selectedCategory}
                  />
                </div>

                <button
                  type="button"
                  className="purchase-enquiry-search-vendor-btn"
                  onClick={handleSearchVendor}
                  disabled={selectedSubcategories.length === 0 || isVendorLoading}
                >
                  {isVendorLoading ? "Searching..." : "Search Vendor"}
                </button>
              </div>

              {selectedSubcategories.length > 0 && (
                <div className="purchase-enquiry-selected-tags">
                  <div className="purchase-enquiry-selected-title">Selected Subcategories</div>
                  <div className="purchase-enquiry-tags-container">
                    {selectedSubcategories.map((sub) => {
                      const categoryName = sub?.categoryName || "Unknown";
                      return (
                        <div key={sub.id} className="purchase-enquiry-chip">
                          <span className="purchase-enquiry-chip-text">
                            {categoryName} › {getSubcategoryLabel(sub)}
                          </span>
                          <button
                            type="button"
                            className="purchase-enquiry-chip-remove"
                            onClick={() => removeSubcategory(sub.id)}
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedCategory && vendors.length === 0 && (
                <div className="purchase-enquiry-sub-list">
                  {filteredSubcategories.length === 0 ? (
                    <div className="purchase-enquiry-empty">No subcategories found</div>
                  ) : (
                    filteredSubcategories.map((subcategory) => {
                      const checked = selectedSubcategories.some(
                        (item) => item.id === subcategory.id
                      );
                      return (
                        <label key={subcategory.id} className="purchase-enquiry-sub-item">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSubcategory(subcategory)}
                          />
                          <span>{getSubcategoryLabel(subcategory)}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              )}

              {vendors.length > 0 && (
                <div className="purchase-enquiry-vendor-list">
                  <div className="purchase-enquiry-vendor-title">Vendors</div>
                  <div className="purchase-enquiry-vendor-options">
                    {vendors.map((vendor, index) => (
                      <div
                        key={vendor?.tran_id || `${getOptionLabel(vendor)}-${index}`}
                        className="add-item-dropdown-option"
                        onClick={() => {
                          onCompanySelect(vendor);
                          onClose();
                        }}
                      >
                        {getOptionLabel(vendor)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const derivedSubtitle = (heading || "")
    .replace(/^please\s+select\s+/i, "")
    .trim();
  const modalSubheading =
    subheading ||
    (derivedSubtitle
      ? `Choose a ${derivedSubtitle.toLowerCase()} to continue`
      : "Choose an option to continue");

  return (
    <>
      <div className="modal-overlay" style={{ zIndex: 1000 }}>
        <div className="modal-content" ref={modalRef}>
          <div className="modal-header">
            <div className="modal-header-info">
              <span className="modal-header-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                  <path d="M3 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v3h6a1 1 0 0 1 1 1v12h-4v-4h-2v4H3Zm2-2h2v-2H5v2Zm0-4h2v-2H5v2Zm0-4h2V9H5v2Zm0-4h2V5H5v2Zm4 12h2v-2H9v2Zm0-4h2v-2H9v2Zm0-4h2V9H9v2Zm0-4h2V5H9v2Zm7 12h2v-2h-2v2Zm0-4h2v-2h-2v2Z" />
                </svg>
              </span>
              <div className="modal-header-text">
                <h2>{heading}</h2>
                <p className="modal-header-subtitle">{modalSubheading}</p>
              </div>
            </div>
            <button
              className="close-button"
              onClick={onClose}
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          <form onSubmit={handleSubmit} className="add-new-form-container">
            <div className="form-group">
              {placeholder && (
                <label className="company-field-label">{placeholder}</label>
              )}
              <div className="input-with-arrow" onClick={handleInputClick}>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  placeholder={`Select ${(placeholder || "option").toLowerCase()}`}
                  readOnly
                  className="company-input"
                />
                <span className="dropdown-arrow" aria-hidden="true">
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </div>

              {isDropdownOpen && (
                <div className="dropdown">
                  <div className="dropdown-search">
                    <span className="dropdown-search-icon" aria-hidden="true">
                      <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="11" cy="11" r="7" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      placeholder={searchPlaceholder}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="dropdown-search-input"
                    />
                  </div>

                  {(customerType === "customer" ||
                    customerType === "vendor") && (
                      <div
                        className="add-item-dropdown-option add-new-customer"
                        onClick={handleAddNewCustomer}
                      >
                        + Add New{" "}
                        {customerType === "vendor" ? "Vendor" : "Customer"}
                      </div>
                    )}

                  {options
                    ?.filter(Boolean)
                    .filter((option) => {
                      const name =
                        customerType === "product"
                          ? option?.label || option
                          : option?.display_name;

                      return name?.toLowerCase().includes(searchTerm.toLowerCase());
                    })
                    .map((option, index) => (
                      <div
                        key={index}
                        className="add-item-dropdown-option"
                        onClick={() => {
                          const name =
                            customerType === "product"
                              ? option?.label || option
                              : option?.display_name;

                          setFormData({ ...formData, companyName: name });
                          onCompanySelect(option);
                          setDropdownOpen(false);
                        }}
                      >
                        {customerType === "product"
                          ? option?.label || option
                          : option?.display_name}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </form>
        </div>
      </div>

      <Modal
        isOpen={showMasterModal}
        onRequestClose={handleMasterModalClose}
        className="transaction-other-detail-custom-modal"
        overlayClassName="transaction-other-detail-custom-overlay"
      >
        {renderMasterModal()}
      </Modal>
    </>
  );
};

export default AddNewEntryModal;
